import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { computed, observable, action } from 'mobx'

/**
 * View model for the dialog that submits a draft job on its client's behalf.
 */
export class SubmitJobDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * Whether the job is billed outside of the platform.
   */
  @observable
  accessor shouldBillExternal = true

  constructor(
    private readonly _job: Job,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _canUpdateJobAsAdmin: () => boolean,
  ) {
    this.shouldBillExternal = _job.billingType === 'External'
  }

  /**
   * Whether the current member can update the job as an admin.
   */
  @computed
  get canUpdateJobAsAdmin() {
    return this._canUpdateJobAsAdmin()
  }

  /**
   * Whether the billing is enabled.
   */
  @computed
  get isBillingEnabled() {
    return this._job.isBillingEnabled
  }

  /**
   * Whether the submitter can choose to bill the job outside of the platform.
   */
  @computed
  get canBillExternal() {
    return this.isBillingEnabled && this.canUpdateJobAsAdmin
  }

  /**
   * The display name of the Pro the client requested, if any.
   */
  @computed
  get requestedProName() {
    return this._job.requestedPro?.displayName
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.submit.reset()

    this.modal.show()
  }

  /**
   * Submits the job.
   */
  submit = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Submitting job', 'Hang tight!')
      .show()
      .autoDismiss()

    try {
      const submissionId = await this._draftJobStore.submit({
        id: this._job.id,
        // Only people who can update jobs as admin can ever set billing to external
        bypassBilling: this.canUpdateJobAsAdmin && this.shouldBillExternal,
      })
      await this._draftJobStore.waitForSubmissionOutcome({ submissionId })

      this.modal.close()

      alert
        .withState('success')
        .saying('Success!', 'Job has been submitted!')
        .autoDismiss()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Submitting the job failed')(err)
    }
  })
}
